import { useState } from "react";
import type { InitializerObjectHierarchy } from "../StartWorkflowFromObjectDialog.types";

export type HierarchyState = {
  currentIndex: number;
  selectedObjectInstanceIdHierarchy: string[];
  selectedObjectInstanceUidHierarchy: string[];
  currentSelectedObjectInstanceId: string;
  previousSelectedObjectInstanceId?: string;
  previousSelectedObjectInstanceUid?: string;
  previousObjectTypeHierarchy?: {
    tag: string;
    name: string;
  };
};

export type HierarchyActions = {
  onNext: () => void;
  onPrevious: () => void;
  onSelectObjectInstanceIdHierarchy: (
    objectInstanceId: string,
    objectInstanceUid: string
  ) => void;
};

type UseHierarchyNavigationProps = {
  initializerObjectHierarchy: InitializerObjectHierarchy[];
  preSelectedObjectHierarchy: {
    objectTypeTag: string;
    objectInstanceId: string;
    objectInstanceUid: string;
  }[];
};

export const useHierarchyNavigation = (
  props: UseHierarchyNavigationProps
): { state: HierarchyState; actions: HierarchyActions } => {
  const { initializerObjectHierarchy, preSelectedObjectHierarchy } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [
    selectedObjectInstanceIdHierarchy,
    setSelectedObjectInstanceIdHierarchy
  ] = useState<string[]>([]);

  const [
    selectedObjectInstanceUidHierarchy,
    setSelectedObjectInstanceUidHierarchy
  ] = useState<string[]>([]);

  const currentSelectedObjectInstanceId =
    selectedObjectInstanceIdHierarchy[currentIndex];

  const {
    previousSelectedObjectInstanceId,
    previousSelectedObjectInstanceUid,
    previousObjectTypeHierarchy
  } = getPreviousStates(
    initializerObjectHierarchy,
    selectedObjectInstanceIdHierarchy,
    selectedObjectInstanceUidHierarchy,
    preSelectedObjectHierarchy,
    currentIndex
  );

  const hasNext = currentIndex < initializerObjectHierarchy.length - 1;
  const hasPrevious = currentIndex > 0;

  const actions: HierarchyActions = {
    onNext: () => hasNext && setCurrentIndex(prev => prev + 1),
    onPrevious: () => hasPrevious && setCurrentIndex(prev => prev - 1),
    onSelectObjectInstanceIdHierarchy: (
      objectInstanceId: string,
      objectInstanceUid: string
    ) => {
      setSelectedObjectInstanceIdHierarchy(prev => {
        const next = [...prev];
        next[currentIndex] = objectInstanceId;
        return next;
      });
      setSelectedObjectInstanceUidHierarchy(prev => {
        const next = [...prev];
        next[currentIndex] = objectInstanceUid;
        return next;
      });
    }
  };

  return {
    state: {
      currentIndex,
      selectedObjectInstanceIdHierarchy,
      selectedObjectInstanceUidHierarchy,
      currentSelectedObjectInstanceId,
      previousSelectedObjectInstanceId,
      previousSelectedObjectInstanceUid,
      previousObjectTypeHierarchy
    },
    actions
  };
};

function getPreviousStates(
  initializerObjectHierarchy: InitializerObjectHierarchy[],
  selectedObjectInstanceIdHierarchy: string[],
  selectedObjectInstanceUidHierarchy: string[],
  preSelectedObjectHierarchy: {
    objectTypeTag: string;
    objectInstanceId: string;
    objectInstanceUid: string;
  }[],
  currentIndex: number
) {
  // Extract base values without pre-selection
  const baseState = {
    previousSelectedObjectInstanceId:
      currentIndex > 0
        ? selectedObjectInstanceIdHierarchy[currentIndex - 1]
        : undefined,
    previousObjectTypeHierarchy:
      currentIndex > 0
        ? initializerObjectHierarchy[currentIndex - 1]
        : undefined,
    previousSelectedObjectInstanceUid:
      currentIndex > 0
        ? selectedObjectInstanceUidHierarchy[currentIndex - 1]
        : undefined
  };

  // If no pre-selected hierarchy, return base state
  if (preSelectedObjectHierarchy.length === 0) {
    return baseState;
  }

  const lastPreSelectedObjectHierarchy = preSelectedObjectHierarchy.at(-1);

  // If no last pre-selected object hierarchy, return base state
  if (!lastPreSelectedObjectHierarchy) {
    return baseState;
  }

  const previousSelectedObjectInstanceId =
    currentIndex === 0
      ? lastPreSelectedObjectHierarchy.objectInstanceId
      : baseState.previousSelectedObjectInstanceId;

  const previousObjectTypeHierarchy = {
    name: lastPreSelectedObjectHierarchy.objectTypeTag,
    tag: lastPreSelectedObjectHierarchy.objectTypeTag
  };

  const previousSelectedObjectInstanceUid =
    currentIndex === 0
      ? lastPreSelectedObjectHierarchy?.objectInstanceUid
      : baseState.previousSelectedObjectInstanceUid;

  return {
    previousSelectedObjectInstanceId,
    previousSelectedObjectInstanceUid,
    previousObjectTypeHierarchy
  };
}
